/**
 * @description Manages language preferences and persistence across browser sessions
 * @class LangHandler
 * @export
 */
export default class LangHandler {
  /**
   * @type {string}
   * @description The user's preferred language code (e.g., 'en-ca', 'fr-ca')
   */
  #preferredLanguage;

  /**
   * Creates an instance of LangHandler.
   * Initializes language preference from localStorage or falls back to the HTML lang attribute.
   * If stored preference differs from current page language, updates the stored preference.
   * @memberof LangHandler
   */
  constructor() {
    this.#preferredLanguage = localStorage.getItem("preferredLanguage") || document.documentElement.lang;
    if (localStorage.getItem("preferredLanguage") !== document.documentElement.lang) {
      this.setLang(this.#preferredLanguage);
      this.#redirect(this.#preferredLanguage);
    }

    // Bind language toggle buttons
    this.#bindLanguageButtons();
  }

  /**
   * @description Binds click handlers to the language toggle buttons in the header
   * @returns {void}
   * @memberof LangHandler
   */
  #bindLanguageButtons() {
    // Bind English button
    const englishButton = document.querySelector(".language-toggle.language-en");
    if (englishButton) {
      englishButton.addEventListener("click", () => {
        this.setLang("en-ca");
        this.#redirect("en-ca");
      });
    }

    // Bind French button
    const frenchButton = document.querySelector(".language-toggle.language-fr");
    if (frenchButton) {
      frenchButton.addEventListener("click", () => {
        this.setLang("fr-ca");
        this.#redirect("fr-ca");
      });
    }
  }

  /**
   * @description Sets the user's preferred language and persists it to localStorage
   * @param {string} lang - The language code to set (e.g., 'en-ca', 'fr-ca')
   * @returns {void}
   * @memberof LangHandler
   */
  setLang(lang) {
    this.#preferredLanguage = lang;
    localStorage.setItem("preferredLanguage", lang);
  }

  /**
   * @description Retrieves the user's preferred language
   * @returns {string} The language code (e.g., 'en-ca', 'fr-ca')
   * @memberof LangHandler
   */
  getLang() {
    return this.#preferredLanguage;
  }

  /**
   * @description Redirects the user to the appropriate language version of the current page
   * @param {string} lang - The target language code (e.g., 'en-ca', 'fr-ca')
   * @returns {void}
   * @memberof LangHandler
   */
  #redirect(lang) {
    const currentPath = window.location.pathname;
    const isOnFrenchPath = currentPath.startsWith("/fr");
    const needsFrench = lang.startsWith("fr");

    let newPath;

    // If we need French but aren't on a French path, add /fr prefix
    if (needsFrench && !isOnFrenchPath) {
      newPath = `/fr${currentPath}`;
    }
    // If we need English but are on a French path, remove /fr prefix
    else if (!needsFrench && isOnFrenchPath) {
      newPath = currentPath.substring(3) || "/";
    }
    // Otherwise, we're already on the correct language path
    else {
      return; // No redirect needed
    }

    // Preserve query string and hash
    window.location.href = newPath + window.location.search + window.location.hash;
  }
}

/**
 * @type {LangHandler}
 * @description Global singleton instance of LangHandler
 */
const langHandler = new LangHandler();

export { langHandler };
